import React, { useEffect, useState, useCallback } from 'react';
import { Input, Button, Tooltip } from 'antd';
import { PlusOutlined, CloseOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

type IslandGrid = {
  islandId: string;
  islandNumber: number | null;
  cells: boolean[][];
  rows: number;
  cols: number;
};

interface FuelPumpLayoutProps {
  onLayoutChange?: (layout: (IslandGrid | null)[][]) => void;
  initialLayout?: (IslandGrid | null)[][];
  isViewMode?: boolean;
  maxRows?: number;
  maxCols?: number;
  cellRows?: number;
  cellCols?: number;
}

const FuelPumpLayout: React.FC<FuelPumpLayoutProps> = ({
  onLayoutChange,
  initialLayout,
  isViewMode = false,
  maxRows = 3,
  maxCols = 3,
  cellRows = 2,
  cellCols = 3,
}) => {
  const { t } = useTranslation('price');
  const [gridMatrix, setGridMatrix] = useState<(IslandGrid | null)[][]>(initialLayout || []);
  const [isInitialized, setIsInitialized] = useState(false);

  const initializeDefaultGrid = useCallback(() => {
    // Initialize empty matrix
    const emptyMatrix: (IslandGrid | null)[][] = Array(maxRows)
      .fill(null)
      .map(() => Array(maxCols).fill(null));

    // Add first island at [0][0]
    const defaultIsland: IslandGrid = {
      islandId: `island_0_0`,
      islandNumber: null,
      cells: Array(cellRows)
        .fill(null)
        .map(() => Array(cellCols).fill(false)),
      rows: cellRows,
      cols: cellCols,
    };
    emptyMatrix[0][0] = defaultIsland;

    setGridMatrix(emptyMatrix);
    setIsInitialized(true);
  }, [maxRows, maxCols, cellRows, cellCols]);

  useEffect(() => {
    if (!isInitialized) {
      if (initialLayout && initialLayout.length > 0) {
        setGridMatrix(initialLayout);
      } else {
        initializeDefaultGrid();
      }
      setIsInitialized(true);
    }
  }, [isInitialized, initialLayout, initializeDefaultGrid]);

  const canAddRight = useCallback(
    (rowIdx: number, colIdx: number): boolean => {
      // Check if within bounds
      if (colIdx + 1 >= maxCols) return false;
      // Check if right cell is empty
      return gridMatrix[rowIdx]?.[colIdx + 1] === null;
    },
    [gridMatrix, maxCols]
  );

  const canAddBottom = useCallback(
    (rowIdx: number, colIdx: number): boolean => {
      // Check if within bounds
      if (rowIdx + 1 >= maxRows) return false;
      // Check if bottom cell is empty
      return gridMatrix[rowIdx + 1]?.[colIdx] === null;
    },
    [gridMatrix, maxRows]
  );

  const addIslandRight = useCallback(
    (rowIndex: number, colIndex: number) => {
      if (!canAddRight(rowIndex, colIndex)) return;

      setGridMatrix((prevGridMatrix) => {
        const newGridMatrix = prevGridMatrix.map((row) => [...row]);
        const newIsland: IslandGrid = {
          islandId: `island_${rowIndex}_${colIndex + 1}`,
          islandNumber: null,
          cells: Array(cellRows)
            .fill(null)
            .map(() => Array(cellCols).fill(false)),
          rows: cellRows,
          cols: cellCols,
        };

        newGridMatrix[rowIndex][colIndex + 1] = newIsland;

        onLayoutChange?.(newGridMatrix);
        return newGridMatrix;
      });
    },
    [canAddRight, cellRows, cellCols, onLayoutChange]
  );

  const addIslandBottom = useCallback(
    (rowIndex: number, colIndex: number) => {
      if (!canAddBottom(rowIndex, colIndex)) return;

      setGridMatrix((prevGridMatrix) => {
        const newGridMatrix = prevGridMatrix.map((row) => [...row]);
        const newIsland: IslandGrid = {
          islandId: `island_${rowIndex + 1}_${colIndex}`,
          islandNumber: null,
          cells: Array(cellRows)
            .fill(null)
            .map(() => Array(cellCols).fill(false)),
          rows: cellRows,
          cols: cellCols,
        };

        newGridMatrix[rowIndex + 1][colIndex] = newIsland;

        onLayoutChange?.(newGridMatrix);
        return newGridMatrix;
      });
    },
    [canAddBottom, cellRows, cellCols, onLayoutChange]
  );

  const toggleCell = useCallback(
    (rowIdx: number, colIdx: number, cellRow: number, cellCol: number) => {
      setGridMatrix((prevGridMatrix) => {
        const newGridMatrix = prevGridMatrix.map((row) =>
          row.map((island) => {
            if (!island) return null;
            return {
              ...island,
              cells: island.cells.map((cellRow) => [...cellRow]),
            };
          })
        );

        const island = newGridMatrix[rowIdx][colIdx];
        if (island) {
          island.cells[cellRow][cellCol] = !island.cells[cellRow][cellCol];
        }

        onLayoutChange?.(newGridMatrix);
        return newGridMatrix;
      });
    },
    [onLayoutChange]
  );

  const updateIslandNumber = useCallback(
    (rowIdx: number, colIdx: number, value: string) => {
      const numValue = value === '' ? null : parseInt(value, 10);

      if (numValue !== null && isNaN(numValue)) return;

      setGridMatrix((prevGridMatrix) => {
        const newGridMatrix = prevGridMatrix.map((row) => [...row]);
        const island = newGridMatrix[rowIdx][colIdx];
        if (island) {
          island.islandNumber = numValue;
        }

        onLayoutChange?.(newGridMatrix);
        return newGridMatrix;
      });
    },
    [onLayoutChange]
  );

  const deleteIsland = useCallback(
    (rowIdx: number, colIdx: number) => {
      setGridMatrix((prevGridMatrix) => {
        const newGridMatrix = prevGridMatrix.map((row) => [...row]);
        newGridMatrix[rowIdx][colIdx] = null;

        onLayoutChange?.(newGridMatrix);
        return newGridMatrix;
      });
    },
    [onLayoutChange]
  );

  const renderIslandGrid = (island: IslandGrid, rowIdx: number, colIdx: number) => {
    const showRightButton = !isViewMode && canAddRight(rowIdx, colIdx);
    const showBottomButton = !isViewMode && canAddBottom(rowIdx, colIdx);

    return (
      <div key={`island_${rowIdx}_${colIdx}`} className="mb-0">
        <div className="mb-2 flex items-center gap-2">
          <label className="text-sm font-semibold">{t('islandNo')}</label>
          <Input
            type="text"
            value={island.islandNumber ?? ''}
            onChange={(e) => {
              const value = e.target.value;
              if (/^\d*$/.test(value)) {
                if (String(value) == '0') return;
                updateIslandNumber(rowIdx, colIdx, value);
              }
            }}
            placeholder={t('enterIslandNo')}
            disabled={isViewMode}
            required
            inputMode="numeric"
            pattern="[1-9][0-9]*"
            style={{ width: '110px' }}
          />
        </div>

        <div className="relative inline-block">
          <div className="grid border-gray-800 p-0" style={{ width: 'fit-content' }}>
            {island.cells.map((row, rIdx) => (
              <div key={rIdx} className="flex gap-0">
                {row.map((cell, cIdx) => (
                  <button
                    key={`${rIdx}_${cIdx}`}
                    onClick={() => !isViewMode && toggleCell(rowIdx, colIdx, rIdx, cIdx)}
                    className={`h-8 w-16 border border-gray-400 transition-colors ${
                      cell ? 'bg-blue-500' : 'bg-white hover:bg-gray-100'
                    } ${isViewMode ? 'cursor-default' : 'cursor-pointer'}`}
                  />
                ))}
              </div>
            ))}
          </div>

          {!isViewMode && !(rowIdx === 0 && colIdx === 0) && (
            <Tooltip title={t('delete') || 'Delete'}>
              <Button
                type="primary"
                danger
                shape="circle"
                icon={<CloseOutlined />}
                size="small"
                onClick={() => deleteIsland(rowIdx, colIdx)}
                className="absolute -left-5 -top-5"
              />
            </Tooltip>
          )}

          {/* Plus icon at right - only if right cell is empty */}
          {showRightButton && (
            <Tooltip title={t('addIslandToRightSide')}>
              <Button
                type="primary"
                shape="circle"
                icon={<PlusOutlined />}
                size="small"
                onClick={() => addIslandRight(rowIdx, colIdx)}
                className="absolute -right-5 top-1/2 -translate-y-1/2"
              />
            </Tooltip>
          )}

          {/* Plus icon at bottom - only if bottom cell is empty */}
          {showBottomButton && (
            <Tooltip title={t('addIslandToBottom')}>
              <Button
                type="primary"
                shape="circle"
                icon={<PlusOutlined />}
                size="small"
                onClick={() => addIslandBottom(rowIdx, colIdx)}
                className="absolute -bottom-5 left-1/2 -translate-x-1/2"
              />
            </Tooltip>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className={`m-0 ${isViewMode ? 'p-2' : 'p-8'}`}>
      <div className="mb-4 space-y-2">
        <h2 className="border-b border-gray-200 pb-3 text-xl font-semibold text-gray-900">
          {t('fuelDispenserVerification')}
        </h2>
      </div>
      <div className="w-full overflow-x-auto">
        <h2 className="text-l pb-3 font-semibold text-gray-700">{t('fuelPumpLayout')}</h2>
        <div className="m-0 min-w-max p-2 pl-6">
          {gridMatrix.map((row, rowIdx) => (
            <div
              key={`row_${rowIdx}`}
              className={`grid grid-cols-${maxCols} mb-5 items-start gap-7`}
            >
              {row.map((island, colIdx) =>
                island ? (
                  renderIslandGrid(island, rowIdx, colIdx)
                ) : (
                  <div key={`empty_${rowIdx}_${colIdx}`} style={{ minWidth: '100px' }} />
                )
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FuelPumpLayout;
